/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useKeycloak } from "@react-keycloak/web";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";

import { ProfileTab } from "enums/ProfileTabs";

import { useSupplierByCode } from "services/input-core/suppliers/get-supplier-by-code";
import { useActivityItemsWithSupplierCode } from "services/input-core/suppliers/get-activity-items";
import { useWorkItemsFromSOW } from "services/input-core/work-items/get-work-items-from-sow";

import GridContainer from "components/UI/Grid/GridContainer";
import GridItemTS from "components/UI/Grid/GridItemTS";
import { TabContent, TabSystem } from "components/UI/TabSystem";
import Header from "./components/Header";

import GeneralTab, { BuildDetailsSection } from "./tabs/GeneralTab/GeneralTab";
import ActivityItemsTab from "./tabs/ActivityItemsTab/ActivityItemsTab";

import { useSubscription } from "store/auth/auth-store";

import { tryRetrieveDateFromUrlParams } from "helpers/DateHelper";
import { useActivityItemsFromSOWWithStacks } from "services/input-core/activity-items/get-activity-item-from-sow-adm";
import {
  Line,
  StyledParagraph,
  StyledParagraphValue,
} from "./tabs/GeneralTab/GeneralTab.styles";

import { useActivityItemContext } from "store/activityItem";
import { LayoutModalInformation } from "components/Modals/layout/LayoutModalInformation";

const SupplierProfile: React.FC = () => {
  const { t } = useTranslation(["supplier-profile", "modals"]);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const supplierCodeFromParams = searchParams.get("id");

  const { isBeltrame, subscription } = useSubscription();
  const { keycloak } = useKeycloak();
  const isBuyer = keycloak?.hasResourceRole("buyer", "uverify");

  const [supplierCode, setSupplierCode] = React.useState(
    supplierCodeFromParams,
  );
  const [date, setDate] = React.useState<Date>(() =>
    tryRetrieveDateFromUrlParams(),
  );

  const { watch, setValue } = useForm({
    defaultValues: {
      billableSowID: undefined,
    },
  });

  const [openModalUndefinedSow, setOpenModalUndefinedSow] = React.useState(
    false,
  );

  const handleGoBack = () => {
    if (window.history.length > 3) {
      navigate(-2);
    } else {
      navigate("/app/dashboard"); // Redireciona para uma rota segura
    }
  };

  const {
    setActivityItems,
    setLoading: setLoadingActivity,
    setRefetching: setRefetchingActivity,
  } = useActivityItemContext();

  const { data, isLoading } = useSupplierByCode(
    supplierCode,
    subscription?.subscriptionId,
    {
      enabled: !!subscription?.subscriptionId && !!supplierCode,
    },
  );

  const { isLoading: isLoadingBillableSows } = useActivityItemsWithSupplierCode(
    supplierCode,
    {
      enabled: isBeltrame && !!supplierCode,
      onSuccess: (data: any) => {
        data?.length <= 0 && setOpenModalUndefinedSow(true);

        return data?.length > 0
          ? setValue("billableSowID", data[0].sowID)
          : undefined;
      },
    },
  );

  const {
    data: activityItemsQuery,
    isLoading: isActivityItemsLoadingQuery,
    isRefetching: isActivityItemsRefetchingQuery,
    isInitialLoading: isActivityItemsInitialLoading,
    refetch: refetchActivityItems,
  } = useActivityItemsFromSOWWithStacks(watch("billableSowID"), date, {
    enabled: isBeltrame && !!watch("billableSowID"),
  });

  const {
    data: workItems,
    isLoading: isWorkItemsLoading,
    refetch: refetchWorkItems,
  } = useWorkItemsFromSOW(watch("billableSowID"), date, {
    enabled: isBeltrame && !!watch("billableSowID"),
  });

  useEffect(() => {
    if (!supplierCode) {
      const auxSupplierCode =
        keycloak?.tokenParsed?.customAttributes?.supplierCode;

      try {
        Array.isArray(JSON.parse(auxSupplierCode)) &&
          setSupplierCode(JSON.parse(auxSupplierCode));
      } catch {
        setSupplierCode(auxSupplierCode);
      }
    }
  }, []);

  useEffect(() => {
    setLoadingActivity(isActivityItemsLoadingQuery);
    setRefetchingActivity(isActivityItemsRefetchingQuery);
    if (activityItemsQuery) setActivityItems(activityItemsQuery);
  }, [
    activityItemsQuery,
    isActivityItemsLoadingQuery,
    isActivityItemsRefetchingQuery,
    setActivityItems,
    setLoadingActivity,
    setRefetchingActivity,
  ]);

  const supplierBuildDetails: BuildDetailsSection[] = [
    {
      title: t("Supplier Details", { ns: "profile" }),
      items: [
        {
          text: t("Supplier Code", { ns: "profile" }),
          value: data?.supplierCode,
        },
        {
          text: t("Supplier Name", { ns: "profile" }),
          value: `${data?.supplierName}`,
        },
        {
          text: t("E-mail", { ns: "profile" }),
          value: data?.supplierCompanyContactPersonEmail,
        },
      ],
    },
    {
      title: t("Secondary Details", { ns: "profile" }),
      items: [
        {
          text: t("Primary Contact Person", { ns: "profile" }),
          value: `${data?.supplierPrimaryContactPerson}`,
        },
        {
          text: t("Primary Services Contact Email", { ns: "profile" }),
          value: data?.supplierPrimaryContactPersonforServicesEmail,
        },
      ],
    },
    {
      title: t("Custom Fields", { ns: "profile" }),
      items: [
        {
          text: t("Fields for Uverify", { ns: "profile" }),
          value: (
            <div>
              {data?.customFields?.map(
                (
                  field: {
                    name: string;
                    value: any;
                  },
                  index: number,
                ) => (
                  <Line key={index}>
                    <StyledParagraph>
                      {t(field?.name, { ns: "custom-fields" })}:
                    </StyledParagraph>
                    {typeof field.value === "object" ? (
                      <StyledParagraphValue>
                        {JSON.stringify(field.value)}
                      </StyledParagraphValue>
                    ) : (
                      <StyledParagraphValue>
                        {t(field.value, { ns: "profile" })}
                      </StyledParagraphValue>
                    )}
                  </Line>
                ),
              )}
            </div>
          ),
        },
      ],
    },
  ];

  const tabs = [
    {
      id: ProfileTab.GENERAL,
      title: t("General", { ns: "profile" }),
    },
    ...(isBeltrame && isBuyer
      ? [
          {
            id: ProfileTab.ACTIVITY_ITEMS,
            title: t("Activity Items", { ns: "profile" }),
          },
        ]
      : []),
  ];

  return (
    <>
      <LayoutModalInformation
        modalOpen={openModalUndefinedSow}
        onClose={() => setOpenModalUndefinedSow(false)}
        modalType="action"
        title="supplier-documentation-missing"
        maxWidth="md"
        buttonClose={false}
        iconTitle="ErrorOutlineOutlined"
        iconColor="red"
        children={t("supplier-documentation-missing-description", {
          ns: "modals",
        })}
        onAction={handleGoBack}
      />
      <GridContainer>
        <GridItemTS xs={12}>
          <Header
            data={{
              fieldglassId: data?.supplierCode,
              name: data?.supplierName,
              status: data?.status,
              zIdentifier: data?.zIdentifier,
              startDate: data?.startDate,
              endDate: data?.endDate,
            }}
            isLoading={isLoading}
            type="supplier"
          />
        </GridItemTS>

        <GridItemTS xs={12} style={{ marginTop: "1rem" }}>
          <TabSystem tabs={tabs} syncWithUrlParams>
            <TabContent value={ProfileTab.GENERAL}>
              <GeneralTab
                isLoading={isLoading}
                buildDetails={supplierBuildDetails}
              />
            </TabContent>

            {isBeltrame && isBuyer && (
              <TabContent value={ProfileTab.ACTIVITY_ITEMS}>
                <ActivityItemsTab
                  fieldglassId={watch("billableSowID")}
                  entityDate={{ start: data?.startDate, end: data?.endDate }}
                  date={date}
                  setDate={setDate}
                  refetchActivityItems={refetchActivityItems}
                  isActivityItemsInitialLoading={isActivityItemsInitialLoading}
                  workItems={workItems}
                  isWorkItemsLoading={
                    isWorkItemsLoading && isLoadingBillableSows
                  }
                  refetchWorkItems={refetchWorkItems}
                  profile={"SOW"}
                  supplierCode={data?.supplierCode ?? supplierCode}
                />
              </TabContent>
            )}
          </TabSystem>
        </GridItemTS>
      </GridContainer>
    </>
  );
};

export default SupplierProfile;
